import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Product, ProductFilters, PaginatedProducts, Brand, Category, ProductVariation } from '@/types/database';

interface ProductRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  short_description: string | null;
  price: number;
  sale_price: number | null;
  sku: string | null;
  stock_status: string;
  stock_quantity: number | null;
  brand_id: string | null;
  featured: boolean;
  images: unknown;
  specifications: unknown;
  meta_title: string | null;
  meta_description: string | null;
  created_at: string;
  updated_at: string;
  brand: Brand | null;
  variations: ProductVariation[];
}

function mapProductRow(row: ProductRow): Product {
  return {
    ...row,
    stock_status: row.stock_status as Product['stock_status'],
    images: Array.isArray(row.images) ? row.images : [],
    specifications: typeof row.specifications === 'object' && row.specifications !== null 
      ? row.specifications as Record<string, string> 
      : {},
    brand: row.brand || undefined,
    variations: row.variations || [],
  };
}

export const useProducts = (filters?: ProductFilters) => {
  const page = filters?.page || 1;
  const pageSize = filters?.pageSize || 25;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  return useQuery({
    queryKey: ['products', filters],
    queryFn: async (): Promise<PaginatedProducts> => {
      // Search for brands matching the search term
      let matchingBrandIds: string[] = [];
      if (filters?.search) {
        const { data: matchingBrands } = await supabase
          .from('brands')
          .select('id');
        
        if (matchingBrands && matchingBrands.length > 0) {
          const filtered = matchingBrands.filter((b: { id: string }) => 
            b.id.toLowerCase().includes(filters.search!.toLowerCase())
          );
          matchingBrandIds = filtered.map((b: { id: string }) => b.id);
        }
      }

      // Build base query for counting
      let countQuery = supabase
        .from('products')
        .select('*', { count: 'exact', head: true });

      // Apply filters to count query
      if (filters?.category && filters.category !== 'all') {
        const { data: categoryData } = await supabase
          .from('categories')
          .select('id')
          .eq('slug', filters.category)
          .maybeSingle();
        
        if (categoryData) {
          // Get products that have this category via junction table
          const { data: productIds } = await supabase
            .from('product_categories')
            .select('product_id')
            .eq('category_id', (categoryData as Category).id);
          
          if (productIds && productIds.length > 0) {
            countQuery = countQuery.in('id', productIds.map((p: { product_id: string }) => p.product_id));
          }
        }
      }

      if (filters?.brand) {
        countQuery = countQuery.eq('brand_id', filters.brand);
      }

      if (filters?.search) {
        countQuery = countQuery.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      const { count } = await countQuery;

      // Build paginated query
      let query = supabase
        .from('products')
        .select(`
          *,
          brand:brands(*),
          variations:product_variations(*)
        `);

      if (filters?.category && filters.category !== 'all') {
        const { data: categoryData } = await supabase
          .from('categories')
          .select('id')
          .eq('slug', filters.category)
          .maybeSingle();
        
        if (categoryData) {
          const { data: productIds } = await supabase
            .from('product_categories')
            .select('product_id')
            .eq('category_id', (categoryData as Category).id);
          
          if (productIds && productIds.length > 0) {
            query = query.in('id', productIds.map((p: { product_id: string }) => p.product_id));
          }
        }
      }

      if (filters?.brand) {
        query = query.eq('brand_id', filters.brand);
      }

      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      const { data, error } = await query
        .order('featured', { ascending: false })
        .order('name')
        .range(from, to);

      if (error) throw error;

      const totalCount = count || 0;
      const totalPages = Math.ceil(totalCount / pageSize);

      return {
        products: (data || []).map((row: unknown) => mapProductRow(row as ProductRow)),
        totalCount,
        totalPages,
        currentPage: page,
      };
    },
  });
};

export const useProduct = (slug: string) => {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          brand:brands(*),
          variations:product_variations(*)
        `)
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Product not found');
      
      return mapProductRow(data as ProductRow);
    },
    enabled: !!slug,
  });
};
